import { Fragment, type JSX, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '../lib/cn';

export interface PageHeaderBreadcrumb {
  label: string;
  to: string;
}

export interface PageHeaderProps {
  breadcrumbs?: PageHeaderBreadcrumb[];
  title: string;
  description?: string;
  /** Decorative: aria-hidden. Does not change the heading's accessible name. */
  icon?: LucideIcon;
  actions?: ReactNode;
  headingLevel?: 'h1' | 'h2';
  className?: string;
}

/**
 * A composable page header: breadcrumbs, title (as a real heading),
 * description, and a quick-actions slot. Plain anchors for breadcrumbs \u2014
 * `packages/ui` has no router dependency, so `to` is rendered as `href`
 * rather than wired to a specific router's `Link`.
 */
export function PageHeader({
  breadcrumbs,
  title,
  description,
  icon: Icon,
  actions,
  headingLevel = 'h1',
  className,
}: PageHeaderProps): JSX.Element {
  const Heading = headingLevel;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {breadcrumbs === undefined || breadcrumbs.length === 0 ? null : (
        <nav aria-label="Breadcrumb">
          <ol className="flex items-center gap-1.5 text-sm text-slate-500">
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;

              return (
                <Fragment key={crumb.to}>
                  {index > 0 ? (
                    <li aria-hidden="true" className="text-slate-300">
                      /
                    </li>
                  ) : null}
                  <li>
                    {isLast ? (
                      <span aria-current="page" className="font-medium text-slate-700">
                        {crumb.label}
                      </span>
                    ) : (
                      <a href={crumb.to} className="hover:text-slate-700 hover:underline">
                        {crumb.label}
                      </a>
                    )}
                  </li>
                </Fragment>
              );
            })}
          </ol>
        </nav>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {Icon === undefined ? null : (
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <Icon aria-hidden="true" className="size-5" />
            </span>
          )}
          <div className="flex flex-col gap-1">
            <Heading className="text-2xl font-semibold text-slate-900">{title}</Heading>
            {description === undefined ? null : (
              <p className="text-sm text-slate-500">{description}</p>
            )}
          </div>
        </div>

        {actions === undefined ? null : <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}
